import React, { useRef, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';

import MapHeader from './components/MapHeader';
import SiteMarker from './components/SiteMarker';
import DogWalkMarker from './components/DogWalkMarker';
import ReviewList from './components/ReviewList';
import ReviewModal from './components/ReviewModal';
import SearchModal from './components/SearchModal';
import MapZoomHandler from './components/MapZoomHandler';
import { Campsite, NormalizedDogWalk } from './types';
import DogWalkSearchLayer from './components/DogWalkSearchLayer';
import { Map as LeafletMap } from 'leaflet';

const UK_CENTER: [number, number] = [54.5, -3];

interface MapProps {
  pois: any[];
  markers: (Campsite | NormalizedDogWalk)[];
  onMapClick: (coords: { latitude: number; longitude: number }) => void;
  onMarkerClick: (index: number) => void;
  fetchNearbySites: (lat: number, lng: number) => void;
}

const Map: React.FC<MapProps> = ({ pois, markers, onMapClick, onMarkerClick, fetchNearbySites }) => {
  const [selectedSite, setSelectedSite] = useState<Campsite | NormalizedDogWalk | null>(null);
  const [selectedWalkForTrail, setSelectedWalkForTrail] = useState<NormalizedDogWalk | null>(null);
  const [reviewingSite, setReviewingSite] = useState<Campsite | NormalizedDogWalk | null>(null);

  // Extract selected site ID for marker highlighting
  const selectedSiteId = selectedSite ? ('ID' in selectedSite ? selectedSite.ID : String(selectedSite.id)) : null;
  const [showSearchModal, setShowSearchModal] = useState(true);
  const [mapCenter, setMapCenter] = useState<{ latitude: number; longitude: number } | null>(null);
  const [showWalks, setShowWalks] = useState(true); // Default to true
  const [showCampsites, setShowCampsites] = useState(true); // Default to true
  const [searchLocation, setSearchLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [map, setMap] = useState<LeafletMap | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const [gpxTrail, setGpxTrail] = useState<[number, number][]>([]);


  const handleSelectLocation = () => setShowSearchModal(true);

  const handleLocationSelect = (lat: number, lng: number) => {
    setSearchLocation({ lat, lng });
    if (map) {
      map.flyTo([lat, lng], 13);
    }
  };

  useEffect(() => {
    if (mapRef.current && !map) {
      setMap(mapRef.current);
    }
  }, [map]);

  // Fetch and parse GPX when a dog walk with gpx_file_path is selected
  useEffect(() => {
    const fetchGpxData = async () => {
      if (selectedWalkForTrail) {
        if (selectedWalkForTrail.gpx_file_path) {
          try {
            // Use our backend proxy to fetch GPX data (avoids CORS issues)
            const proxyUrl = `/api/gpx-proxy?path=${encodeURIComponent(selectedWalkForTrail.gpx_file_path)}`;

            console.log('Fetching GPX via proxy:', proxyUrl);
            const response = await fetch(proxyUrl);

            if (!response.ok) {
              throw new Error(`Failed to fetch GPX: ${response.status}`);
            }

            const gpxText = await response.text();
            const parser = new DOMParser();
            const gpxDoc = parser.parseFromString(gpxText, 'text/xml');

            // Extract track points from GPX
            const trkpts = gpxDoc.querySelectorAll('trkpt');
            const coordinates: [number, number][] = Array.from(trkpts).map(pt => {
              const lat = parseFloat(pt.getAttribute('lat') || '0');
              const lon = parseFloat(pt.getAttribute('lon') || '0');
              return [lat, lon];
            });

            console.log(`Parsed ${coordinates.length} GPS points from GPX`);
            setGpxTrail(coordinates);
          } catch (error) {
            console.error('Error fetching or parsing GPX:', error);
            setGpxTrail([]);
          }
        } else {
          setGpxTrail([]);
        }
      } else {
        setGpxTrail([]);
      }
    };

    fetchGpxData();
  }, [selectedWalkForTrail]);


  return (
    <div className="map-wrapper">
      <div className="map-container">
        <MapHeader
          onSelectLocation={() => setShowSearchModal(true)}
          showWalks={showWalks}
          setShowWalks={setShowWalks}
          showCampsites={showCampsites}
          setShowCampsites={setShowCampsites}
        />

        <MapContainer
          center={UK_CENTER}
          zoom={6}
          className="custom-map"
          ref={mapRef}

        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {mapCenter && <MapZoomHandler center={mapCenter} />}

          <MarkerClusterGroup>
            {markers.filter(site => {
              if ('type' in site && site.type === 'dogwalk') return showWalks;
              return showCampsites;
            }).map(site => {
              // Check if it's a dog walk or campsite
              if ('type' in site && site.type === 'dogwalk') {
                return (
                  <DogWalkMarker
                    key={site.id}
                    walk={site as NormalizedDogWalk}
                    onSelect={setSelectedSite}
                    onShowTrail={setSelectedWalkForTrail}
                    onAddReview={(walk) => setReviewingSite(walk)}
                    isSelected={selectedSiteId === String(site.id)}
                  />
                );
              } else {
                return (
                  <SiteMarker
                    key={(site as Campsite).ID}
                    site={site as Campsite}
                    onSelect={setSelectedSite}
                    onAddReview={(site) => setReviewingSite(site)}
                    isSelected={selectedSiteId === (site as Campsite).ID}
                  />
                );
              }
            })}
          </MarkerClusterGroup>

          {/* Render DogWalksLayer if showWalks is true */}
          {showWalks && searchLocation && (
            <DogWalkSearchLayer
              location={searchLocation}
              onWalkSelect={(walk) => setSelectedSite(walk)}
            />
          )}

          {/* Render GPX trail if available */}
          {gpxTrail.length > 0 && (
            <Polyline
              positions={gpxTrail}
              color="#3388ff"
              weight={4}
              opacity={0.7}
            />
          )}

        </MapContainer>
      </div>

      <div className="sidebar">
        {reviewingSite ? (
          <ReviewModal site={reviewingSite} onClose={() => setReviewingSite(null)} />
        ) : selectedSite ? (
          <ReviewList
            site={selectedSite}
            onClose={() => setSelectedSite(null)}
            onAddReview={setReviewingSite}
          />
        ) : (
          <div style={{ padding: '20px', color: '#666', textAlign: 'center', marginTop: '50px' }}>
            <h3>Welcome to Map Doggle!</h3>
            <p>Select a campsite or dog walk on the map to view details and reviews.</p>
            <div style={{ fontSize: '3em', marginTop: '20px' }}>🐕🏕️</div>
          </div>
        )}
      </div>

      {showSearchModal && (
        <SearchModal
          onClose={() => setShowSearchModal(false)}
          onPlaceSelect={(lat, lng, name) => {
            setMapCenter({ latitude: lat, longitude: lng });
            onMapClick({ latitude: lat, longitude: lng });
            fetchNearbySites(lat, lng);
            setShowSearchModal(false);
            setSearchLocation({ lat, lng });
          }}
        />
      )}

    </div>
  );
};

export default Map;
